import { useCallback, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';

import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';

import { useWMS1060_R01Store } from './Store_WMS1060_R01';
import type { WMS1060_R01Filter } from './Store_WMS1060_R01';
import { downloadExcel_WMS1060_R01, getWMS1060_R01AllCount, getWMS1060_R01List } from "./Api_WMS1060_R01";
import { WMS1060_R01Dto } from './Dto_WMS1060_R01';
import {WMS1020_R01HeaderDto} from "src/containers/mes/WMS/WMS1020_R01/Dto_WMS1020_R01";
import {getWMS1020_R01List} from "src/containers/mes/WMS/WMS1020_R01/Api_WMS1020_R01";

interface Props {
    gridRef: React.RefObject<AgGridReact<WMS1060_R01Dto> | null>;
    tabList: Tab[] | undefined;
    setMessage: (message: string) => void;
}

export const Content_WMS1060_R01 = (props: Props) => {

    const {
        gridRef,
        tabList,
        setMessage,
    } = props;

    // ========================================
    // Store
    // ========================================
    const {
        WMS1060_R01Filter,
        WMS1060_R01PastFilter,
        setWMS1060_R01PastFilter,
        setWMS1060_R01List,
        setAllCount,
        initData,
        setScrollTop,
    } = useWMS1060_R01Store();

    const { setLoadingSpinner } = useLoadingSpinnerStore();

    // 권한 체크
    useFetchActionbarAuth("WMS1060_R01");

    // ========================================
    // 검색 파라미터 변환
    // ========================================
    const getParams = (filter: WMS1060_R01Filter) => {
        const params: Partial<WMS1060_R01Filter> = {
            dateRange: filter.dateRange,
            logicCenter: filter.logicCenter,     // ← 추가
            warehouseCd: filter.warehouseCd,
            inoutType: filter.inoutType,         // ← 추가
            inoutClass: filter.inoutClass,       // ← 추가
            itemGroup: filter.itemGroup,         // ← 추가
            customCd: filter.customCd,
            customNm: filter.customNm,           // ← 추가
            itemCd: filter.itemCd,
            itemNm: filter.itemNm,               // ← 추가
        };
        return params;
    };

    // ========================================
    // 전체 건수 조회
    // ========================================
    const renewalAllCount = useCallback(async () => {
        try {
            const response = await getWMS1060_R01AllCount(getParams(WMS1060_R01Filter));
            setAllCount(response.data.data);
            return response.data.data;
        } catch (error) {
            showError("전체 건수 조회 중 오류가 발생했습니다.");
            return 0;
        }
    }, [WMS1060_R01Filter, setAllCount]);



    // ========================================
    // 데이터 조회
    // ========================================
    const fetchData = useCallback(async () => {
        setLoadingSpinner(true);
        setScrollTop(null);

        try {
            // 전체 건수 조회
            const processAllCount = await renewalAllCount();


            // 목록 조회
            const response = await getWMS1060_R01List(
                'search',
                getParams(WMS1060_R01Filter),
                0
            );

            if (response.data.successFlag) {
                const newList = response.data.data;

                setWMS1060_R01List(newList);
                setWMS1060_R01PastFilter(WMS1060_R01Filter);

                ShowSuccessFetch(
                    setMessage,
                    `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`
                );
            } else {
                showError('조회 중 오류가 발생했습니다.');
            }
        } catch (error) {
            showError('조회 중 오류가 발생했습니다.');
            console.error('WMS1060_R01 fetchData error:', error);
        } finally {
            setLoadingSpinner(false);
        }
    }, [
        WMS1060_R01Filter,
        setWMS1060_R01List,
        setWMS1060_R01PastFilter,
        renewalAllCount,
        setLoadingSpinner,
        setScrollTop,
    ]);

    // ========================================
    // 엑셀 다운로드
    // ========================================
    const handleExcelClick = useCallback(async () => {
        setLoadingSpinner(true);

        try {
            const response = await downloadExcel_WMS1060_R01(
                'search',
                getParams(WMS1060_R01PastFilter)
            );

            if (response.data.successFlag) {
                downloadSettingExcel({
                    responseData: response.data.data,
                    fileName: '출하현황조회.xlsx'
                });
                ShowSuccessFetch(setMessage, '엑셀 다운로드 완료');
            } else {
                showWarning('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        } catch (error) {
            showError('엑셀 다운로드 중 오류가 발생했습니다.');
            console.error('WMS1060_R01 handleExcelClick error:', error);
        } finally {
            setLoadingSpinner(false);
        }
    }, [WMS1060_R01PastFilter, setLoadingSpinner]);

    // ========================================
    // 초기화
    // ========================================
    const initContainerData = useCallback(() => {
        initData();
        setMessage('초기화되었습니다.');
    }, [initData, setMessage]);



    // ========================================
    // Return
    // ========================================
    return {
        fetchData,
        handleExcelClick,
        initContainerData,
    };
};

export default Content_WMS1060_R01;
